import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { filter, map, Observable, of, ReplaySubject } from 'rxjs';
import { isDefined } from '../../components/utils/utils';
import {
  MANAGE_SIDENAV_DEFINITION,
  MenuGroupDef,
  SETTINGS_SIDENAV_DEFINITION,
} from '../../shared/models/menu-group-def';
import { Permission, PermissionType } from '../models/permission';
import { NAVIGATION_DEFINITION } from '../../shared/models/sub-nav-def';
import { SignInService } from '../../common/services/sign-in/sign-in.service';
import { Role } from '../../feature/settings/models/role';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private permissionMapSubject: ReplaySubject<Map<PermissionType, Permission>> = new ReplaySubject<
    Map<PermissionType, Permission>
  >();
  private permissionMap = this.permissionMapSubject.asObservable();
  private entityCodePermissionTypeMapping: Map<string, PermissionType> = new Map<string, PermissionType>();

  constructor(private signInService: SignInService) {
    this.buildEntityCodeMapping(MANAGE_SIDENAV_DEFINITION, undefined, this.entityCodePermissionTypeMapping);
    this.buildEntityCodeMapping(SETTINGS_SIDENAV_DEFINITION, undefined, this.entityCodePermissionTypeMapping);
    this.buildSubNavMapping(this.entityCodePermissionTypeMapping);
    this.signInService
      .getRoles()
      .subscribe((roles: Role[]) => this.permissionMapSubject.next(this.buildPermissionMap(roles)));
  }

  public hasPermission(route: ActivatedRouteSnapshot): Observable<boolean> {
    if (isDefined(route.data.permission)) {
      return this.hasViewPermission(route.data.permission);
    } else {
      return this.hasViewPermission(this.matchPermissionType(route));
    }
  }

  public hasViewPermission(permission: PermissionType): Observable<boolean> {
    if (!isDefined(permission)) {
      return of(false);
    }
    return this.permissionMap.pipe(map((value) => value.get(permission)?.view));
  }

  public hasModifyPermission(permission: PermissionType): Observable<boolean> {
    if (!isDefined(permission)) {
      return of(false);
    }
    return this.permissionMap.pipe(map((value) => value.get(permission)?.modify));
  }

  public hasExportPermission(permission: PermissionType): Observable<boolean> {
    if (!isDefined(permission)) {
      return of(false);
    }
    return this.permissionMap.pipe(map((value) => value.get(permission)?.export));
  }

  public hasManagePermissions(): Observable<boolean> {
    return this.hasPermissionFromList(Permission.MANAGE_PERMISSIONS);
  }

  public hasSettingsPermissions(): Observable<boolean> {
    return this.hasPermissionFromList(Permission.SETTINGS_PERMISSIONS);
  }

  private hasPermissionFromList(permissions: PermissionType[]) {
    return this.permissionMap.pipe(
      map((permissionMap) => permissions.map((value) => permissionMap.has(value)).reduce((acc, val) => acc || val))
    );
  }

  private matchPermissionType(route: ActivatedRouteSnapshot): PermissionType {
    let entityCode = Array.of(
      route.params.navCode,
      route.params.entityCode + route.params.groupId,
      route.params.entityCode
    )
      .filter(isDefined)
      .find((value) => this.entityCodePermissionTypeMapping.has(value));
    if (!isDefined(entityCode)) {
      return this.entityCodePermissionTypeMapping.get(route.routeConfig.path);
    }
    return this.entityCodePermissionTypeMapping.get(entityCode);
  }

  private buildEntityCodeMapping(
    menuGroupDef: MenuGroupDef[],
    permissionType: PermissionType,
    map: Map<string, PermissionType>
  ) {
    menuGroupDef.forEach((menuGroup) => {
      if (isDefined(permissionType) && isDefined(menuGroup.menuItem)) {
        map.set(menuGroup.menuItem, permissionType);
      }
      if (isDefined(permissionType) && isDefined(menuGroup.subNav)) {
        map.set(menuGroup.subNav, permissionType);
      }
      if (isDefined(permissionType) && isDefined(menuGroup.menuItem) && isDefined(menuGroup.subMenuItem)) {
        map.set(menuGroup.menuItem + menuGroup.subMenuItem, permissionType);
      }

      if (isDefined(menuGroup.subGroup)) {
        this.buildEntityCodeMapping(
          menuGroup.subGroup,
          menuGroup.permission ? menuGroup.permission : permissionType,
          map
        );
      }
    });
  }

  private buildSubNavMapping(map: Map<string, PermissionType>) {
    NAVIGATION_DEFINITION.forEach((subNavDef) => {
      const permissionType = this.entityCodePermissionTypeMapping.get(subNavDef.code);
      subNavDef.tabs.forEach((tabDef) => map.set(tabDef.code, permissionType));
    });
  }

  private buildPermissionMap(roles: Role[]): Map<PermissionType, Permission> {
    let permissionMap = new Map<PermissionType, Permission>();
    roles
      .flatMap((value) => value.permissions)
      .forEach((value) =>
        permissionMap.has(value.type)
          ? permissionMap.set(value.type, this.permissionSum(permissionMap.get(value.type), value))
          : permissionMap.set(value.type, value)
      );
    return permissionMap;
  }

  private permissionSum(x: Permission, y: Permission): Permission {
    return { type: x.type, view: x.view || y.view, modify: x.modify || y.modify, export: x.export || y.export };
  }
}
